/* eslint-disable no-debugger */
import { useState, useEffect, useMemo } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import validate from "./MembershipDetailReportValidate";
import makeExpirationCell from './ExpirationCell';
import useReportPeriodFlatData from '@usas/ui-web/state/reportPeriodFlat/UseReportPeriodFlatData';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate, getTodaysDate } from '@usas/ui-web/utils/DateFunctions';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';
import useOrgUnitData from '../../../state/orgUnit/UseOrgUnitData';

// Define the columns configuration for this report
// LMS expiration cells carry a not-applicable reason or "Missing" instead of a date, so no date format on them
const baseColumns = [
  { width: "110px", id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { width: "150px", id: "MemberId", field: "memberId", title: "Member ID" },
  { width: "130px", id: "FirstName", field: "firstName", title: "First Name" },
  { width: "130px", id: "MiddleName", field: "middleName", title: "Middle Name" },
  { width: "130px", id: "LastName", field: "lastName", title: "Last Name" },
  { width: "90px", id: "CompetitionCategory", field: "competitionCategory", title: "Competition Category" },
  { width: "110px", id: "BirthDate", field: "birthDate", title: "Birth Date", format: "{0:MM/dd/yyyy}" },
  { width: "90px", id: "BirthDateConfirmed", field: "birthDateConfirmed", title: "Birth Date Confirmed" },
  { width: "110px", id: "CurrentAge", field: "currentAge", title: "Current Age" },
  { width: "240px", id: "Email", field: "email", title: "Email" },
  { width: "140px", id: "PhoneNumber", field: "phoneNumber", title: "Phone Number" },
  { width: "140px", id: "Race", field: "race", title: "Race" },
  { width: "170px", id: "RaceDetail", field: "raceDetail", title: "Race Detail" },
  { width: "300px", id: "FullAddress", field: "fullAddress", title: "Address" },
  { width: "190px", id: "PrimaryContactName", field: "primaryContactName", title: "Primary Contact Name" },
  { width: "240px", id: "PrimaryContactEmail", field: "primaryContactEmail", title: "Primary Contact Email" },
  { width: "190px", id: "ParentContactName", field: "parentContactName", title: "Parent Contact Name" },
  { width: "240px", id: "ParentContactEmail", field: "parentContactEmail", title: "Parent Contact Email" },
  { width: "140px", id: "CPR", field: "cpr", title: "CPR Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "125px", id: "SafetyTrainingWater", field: "safetyTrainingWater", title: "Safety Training Water Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "125px", id: "SafetyTrainingOnline", field: "safetyTrainingOnline", title: "Safety Training Online Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "120px", id: "Cat", field: "cat", title: "CAT Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "120px", id: "Qcf", field: "qcf", title: "QCF Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "130px", id: "WelcomeToUsas", field: "welcomeToUsas", title: "Welcome To USAS Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "130px", id: "RulesAndRegs", field: "rulesAndRegs", title: "Rules And Regs Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "115px", id: "Core", field: "core", title: "CORE Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "115px", id: "Canra", field: "canra", title: "CANRA Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "115px", id: "Apt", field: "apt", title: "APT Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "115px", id: "BGC", field: "bgc", title: "BGC Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "125px", id: "Concussion", field: "concussion", title: "Concussion Protocol Exp Date", format: "{0:MM/dd/yyyy}" },
  { width: "90px", id: "UsasTenure", field: "usasTenure", title: "USAS Tenure" }
];

// Columns that turn red when the date is before the good-standing as-of date
const EXPIRATION_COLUMN_IDS = new Set([
  'CPR', 'SafetyTrainingWater', 'SafetyTrainingOnline', 'Cat', 'Qcf', 'WelcomeToUsas',
  'RulesAndRegs', 'Core', 'Canra', 'Apt', 'BGC', 'Concussion'
]);

// Sub-report contents are not settled, so no membershipDetail_ columns yet
const excelColumns = [
  { id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { id: "MemberId", field: "memberId", title: "Member ID" },
  { id: "FirstName", field: "firstName", title: "First Name" },
  { id: "MiddleName", field: "middleName", title: "Middle Name" },
  { id: "LastName", field: "lastName", title: "Last Name" },
  { id: "CompetitionCategory", field: "competitionCategory", title: "Competition Category" },
  { id: "BirthDate", field: "birthDate", title: "Birth Date", format: "{0:MM/dd/yyyy}" },
  { id: "BirthDateConfirmed", field: "birthDateConfirmed", title: "Birth Date Confirmed" },
  { id: "CurrentAge", field: "currentAge", title: "Current Age" },
  { id: "Email", field: "email", title: "Email" },
  { id: "PhoneNumber", field: "phoneNumber", title: "Phone Number" },
  { id: "Race", field: "race", title: "Race" },
  { id: "RaceDetail", field: "raceDetail", title: "Race Detail" },
  { id: "FullAddress", field: "fullAddress", title: "Address" },
  { id: "PrimaryContactName", field: "primaryContactName", title: "Primary Contact Name" },
  { id: "PrimaryContactEmail", field: "primaryContactEmail", title: "Primary Contact Email" },
  { id: "ParentContactName", field: "parentContactName", title: "Parent Contact Name" },
  { id: "ParentContactEmail", field: "parentContactEmail", title: "Parent Contact Email" },
  { id: "CPR", field: "cpr", title: "CPR Exp Date" },
  { id: "SafetyTrainingWater", field: "safetyTrainingWater", title: "Safety Training Water Exp Date" },
  { id: "SafetyTrainingOnline", field: "safetyTrainingOnline", title: "Safety Training Online Exp Date" },
  { id: "Cat", field: "cat", title: "CAT Exp Date" },
  { id: "Qcf", field: "qcf", title: "QCF Exp Date" },
  { id: "WelcomeToUsas", field: "welcomeToUsas", title: "Welcome To USA Swimming Expiration" },
  { id: "RulesAndRegs", field: "rulesAndRegs", title: "Rules And Regs Expiration" },
  { id: "Core", field: "core", title: "Core Expiration" },
  { id: "Canra", field: "canra", title: "CANRA Expiration" },
  { id: "Apt", field: "apt", title: "APT Expiration" },
  { id: "Bgc", field: "bgc", title: "Background Check Expiration" },
  { id: "Concussion", field: "concussion", title: "Concussion Expiration" },
  { id: "UsasTenure", field: "usasTenure", title: "USA Swimming Tenure" },
  { id: "RoleDetails_AgeAtRegistration", field: "roleDetails_ageAtRegistration", title: "Age At Registration" },
  { id: "RoleDetails_AgeAtSeasonEnd", field: "roleDetails_ageAtSeasonEnd", title: "Age At Season End" },
  { id: "RoleDetails_ClubCode", field: "roleDetails_clubCode", title: "Club Code" },
  { id: "RoleDetails_ClubName", field: "roleDetails_clubName", title: "Club Name" },
  { id: "RoleDetails_ClubRoleTenure", field: "roleDetails_clubRoleTenure", title: "Club Role Tenure" },
  { id: "RoleDetails_ClubTenure", field: "roleDetails_clubTenure", title: "Club Tenure" },
  { id: "RoleDetails_EffectiveDate", field: "roleDetails_effectiveDate", title: "Effective Date" },
  { id: "RoleDetails_ExpirationDate", field: "roleDetails_expirationDate", title: "Expiration Date" },
  { id: "RoleDetails_GoodStanding", field: "roleDetails_goodStanding", title: "Good Standing" },
  { id: "RoleDetails_Level1Role", field: "roleDetails_level1Role", title: "Level 1 Role" },
  { id: "RoleDetails_Level4Role", field: "roleDetails_level4Role", title: "Level 4 Role" },
  { id: "RoleDetails_LscCode", field: "roleDetails_lscCode", title: "LSC Code" },
  { id: "RoleDetails_RoleTenure", field: "roleDetails_roleTenure", title: "Role Tenure" }
];

const useMembershipDetailReport = () => {
  const { reportPeriodFlatState, getReportPeriodsFlatByOrganizationId } = useReportPeriodFlatData();
  const [seasonYearState, setSeasonYearState] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [modalState, setModalState] = useState(getInitialModalState());
  const { orgUnitState } = useOrgUnitData();

  const {
    formState,
    errorState,
    handleSubmit,
    updateFormState,
    onFormValueChanged,
    onValueTextPairChanged,
    setFormState,
    setIsDirty
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  const columns = useMemo(() => {
    let refDate = formState.goodStandingAsOfDate === Constants.BLANK_DATE_STRING ? formatDate(getTodaysDate()) : formState.goodStandingAsOfDate;

    const cells = { data: makeExpirationCell(refDate) };

    return baseColumns.map(column =>
      EXPIRATION_COLUMN_IDS.has(column.id) ? { ...column, cells } : column
    );
  }, [formState.goodStandingAsOfDate]);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
    setIsDirty(true);
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: '',
      seasonId: -1,
      seasonYear: -1,
      minAge: 1,
      maxAge: 99,
      genders: [{ id: 1, name: 'Female' }, { id: 2, name: 'Male' }],
      nationalJuniorTeamOnly: false,
      nationalTeamOnly: false,
      goodStandingAsOfDate: formatDate(getTodaysDate()),
      membersAsOfDate: Constants.BLANK_DATE_STRING,
      birthdateConfirmedOnly: false,
      clubs: [],
      lscs: [],
      zones: [],
      displayOptions: true,
      nationalTeamGroupId: -1,
      nationalTeamGroupName: '',
      orgGroupId: Constants.DEFAULT_ID,
      groupName: '',
      memberId: '',
      personId: Constants.DEFAULT_ID,
      orgRole: [],
      exportDetailData: false
    };
  }

  useEffect(() => {
    if (reportPeriodFlatState.isLoading === false && reportPeriodFlatState.isLoaded === false) {
      getReportPeriodsFlatByOrganizationId(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportPeriodFlatState.isLoaded === true) {
      const years = [];

      reportPeriodFlatState.dataAsOptionsWithNames.map(rp => {
        if (rp.name.substring(5, 6) === '(') {
          years.unshift({ id: rp.id, name: rp.name.substring(0, 4) });
        }
      });
      years.unshift({ id: -1, name: '--' });
      setSeasonYearState({ isLoading: false, isLoaded: true, options: years });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodFlatState]);

  function getClubFileterIds() {
    if (orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 2) { 
      return formState.orgUnitId.toString();
    } else  {
      return formState.lscs.map(c => c.id).join(',')
    } 
  }

  return {
    formState,
    errorState,
    setFormState,
    reportParameters: {
      OrgUnitId_Int: formState.orgUnitId === "" ? null : formState.orgUnitId,
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','), 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      Genders: formState.genders.map(gender => gender.id).join(','), 
      SeasonYear_Int: formState.seasonYear === Constants.DEFAULT_ID ? null : parseInt(formState.seasonYear, 10),
      MembersAsOfDate: formState.membersAsOfDate === Constants.BLANK_DATE_STRING ? null : formState.membersAsOfDate,
      MinAge_Int: ToIntIfInt(formState.minAge), 
      MaxAge_Int: ToIntIfInt(formState.maxAge), 
      MemberTypeId_Int: formState.orgRole.length > 0 ? formState.orgRole[0].id : null, //comes from treeview
      NtOnly_Int: formState.nationalTeamOnly === true ? 1 : 0, 
      NjtOnly_Int: formState.nationalJuniorTeamOnly === true ? 1 : 0, 
      Group_Int: formState.orgGroupId === Constants.DEFAULT_ID ? null : formState.orgGroupId,
      BdConfirmed_Int: formState.birthdateConfirmedOnly === true ? 1 : 0      
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    excelColumns: excelColumns,
    clubFilterIds: getClubFileterIds()
  };
};

export default useMembershipDetailReport;